import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import BlankImage from '../../../../assets/images/blank-image.png';
import { ToggleSwitch } from '../../../../components/common/inputs/ToggleSwitch';
import { MachineApprovalStatusType } from '../../../../constants/machine/machine.constant';
import {
  OpenMarketProductApporovalStatusType,
  OpenMarketProductApprovalStatusConstant,
  OpenMarketProductStatusConstant,
  OpenMarketProductStatusConstantType,
} from '../../../../constants/openMarketProduct/openMarketProduct';
import { usePatchUserSellTractorsMutation } from '../../../../hooks/api/user/mutations/usePatchUserSellTractors.mutation';
import { useCheckUserLoggedIn } from '../../../../hooks/user/useCheckUserLoggedIn';
import { IOpenMarketProduct } from '../../../../interfaces/OpenMarketProduct.interfaces';
import { isNewProduct } from '../../../../utils/calculator/calculateDate.util';
import { getOpenMarketProductTitle } from '../../../../utils/getter/getOpenMarketProductTitle.util';
import { getOpenMarketDetailPageURL } from '../../../../utils/getter/getProductDetailPageURL.util';
import { getOpenMarketProductPrice } from '../../../../utils/getter/getProductPrice.util';
import { getRegionByDepth, RegionDepth } from '../../../../utils/getter/getShortAddress.util';
import { LoginToCheckPrice } from '../components/LoginToCheckPrice';
import { ProductChip } from '../components/ProductChip';

export type IUserSellProductForDetailCard = Pick<
  IOpenMarketProduct,
  | 'id'
  | 'updatedAt'
  | 'views'
  | 'inquiryCount'
  | 'images'
  | 'machine'
  | 'manufacturedYear'
  | 'location'
  | 'operationTime'
  | 'approvalStatus'
  | 'status'
  | 'approvalDate'
  | 'hopeSellingPrice'
  | 'priceNegotiationYn'
>;

interface UserSellProductDetailCardProps {
  product: IUserSellProductForDetailCard;
  isNewBadgeVisible?: boolean;
}

const LIKE_NEW_OPERATION_HOURS = 200;

export const UserSellProductDetailCard = ({ product, isNewBadgeVisible = false }: UserSellProductDetailCardProps) => {
  const navigate = useNavigate();
  const { isUserLoggedIn } = useCheckUserLoggedIn();

  const { APPROVAL, ONGOING, REJECT } = OpenMarketProductApprovalStatusConstant;
  const { ONSALE, SOLDOUT } = OpenMarketProductStatusConstant;

  const { mutate: patchUserSellTractor, isLoading: isPatchUserSellTractorLoading } = usePatchUserSellTractorsMutation();
  const queryClient = useQueryClient();

  const handleClick = () => {
    if (
      product.approvalStatus === APPROVAL &&
      product.status === ONSALE &&
      product.machine.approvalStatus === 'APPROVAL'
    ) {
      const url = getOpenMarketDetailPageURL(product.id, product.machine.machineType);
      navigate(url);
    }
  };

  const getBlurEffect = (
    status: OpenMarketProductStatusConstantType,
    approvalStatus: OpenMarketProductApporovalStatusType,
    machineStatus: MachineApprovalStatusType,
  ) => {
    const BLURRED = true;
    const NOT_BLURRED = false;
    const DIMMED_TEXT = {
      SOLDOUT: '판매 완료',
      ONGOING: '심사중',
      REJECT: '판매불가',
      DEFAULT: '',
    };

    switch (approvalStatus) {
      case APPROVAL:
        return {
          isBlurred:
            status === SOLDOUT
              ? BLURRED
              : machineStatus === 'PSWAITING' || machineStatus === 'WAITING'
                ? BLURRED
                : NOT_BLURRED,
          dimmedText:
            status === SOLDOUT
              ? DIMMED_TEXT.SOLDOUT
              : machineStatus === 'PSWAITING' || machineStatus === 'WAITING'
                ? DIMMED_TEXT.ONGOING
                : DIMMED_TEXT.DEFAULT,
        };

      case ONGOING:
        return {
          isBlurred: BLURRED,
          dimmedText: DIMMED_TEXT.ONGOING,
        };

      case REJECT:
        return {
          isBlurred: BLURRED,
          dimmedText: DIMMED_TEXT.REJECT,
        };

      default:
        return {
          isBlurred: NOT_BLURRED,
          dimmedText: DIMMED_TEXT.DEFAULT,
        };
    }
  };
  const blurEffect = getBlurEffect(product.status, product.approvalStatus, product.machine?.approvalStatus);
  const { isBlurred, dimmedText } = blurEffect;

  const isNewOpenMarketProduct = isNewProduct(new Date(product.approvalDate), product.status === ONSALE);

  const title = getOpenMarketProductTitle({ brandName: product.machine?.brand?.name, machine: product.machine });

  const price = useMemo<string>(() => {
    return getOpenMarketProductPrice(product.status, product.hopeSellingPrice);
  }, [product]);

  const productImageUrl = useMemo(() => {
    if (!product.images) {
      return BlankImage;
    }

    if (product.images.length === 0) {
      return BlankImage;
    }

    if (product.images.find((image) => image.type === 'LEFT')) {
      return product.images.find((image) => image.type === 'LEFT')?.imageUrl;
    }

    return product.images[0]?.imageUrl;
  }, [product.images]);

  return (
    <Container onClick={handleClick}>
      <ImageWrap>
        <Image src={productImageUrl} alt={productImageUrl} isBlurred={isBlurred} loading={'lazy'} />
        {isBlurred && <DimmedText>{dimmedText}</DimmedText>}
        {isNewBadgeVisible && isNewOpenMarketProduct && (
          <BadgeContainer>
            <ProductChip type={'NEW'} />
          </BadgeContainer>
        )}
      </ImageWrap>

      <ProductInfoSection isBlurred={isBlurred}>
        <ProductContentGroup>
          <Title>{title}</Title>
          {isUserLoggedIn && <Price>{price}</Price>}
          {!isUserLoggedIn && <LoginToCheckPrice />}
          <LocationAndTimeContainer>
            <TractorSummaryText>{`${getRegionByDepth(product.location, RegionDepth.REGION)} · ${String(
              product.manufacturedYear,
            ).slice(2, 4)}년식 · ${Number(product.operationTime).toLocaleString()}시간`}</TractorSummaryText>
          </LocationAndTimeContainer>
        </ProductContentGroup>
        {!isBlurred && (
          <BottomSection>
            <SuggestPriceToggleSection
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <SuggestPriceTextSection>
                <SuggestPriceTitle>{'가격 제안'}</SuggestPriceTitle>
                <SuggestPriceDescription>{product.priceNegotiationYn ? '받는중' : '안 받는중'}</SuggestPriceDescription>
              </SuggestPriceTextSection>
              <ToggleSwitch
                isChecked={!!product.priceNegotiationYn}
                onClick={() => {
                  patchUserSellTractor({
                    sellTractorId: product.id,
                    reqBody: {
                      priceNegotiationYn: !product.priceNegotiationYn,
                      approvalStatus: product.approvalStatus,
                      status: product.status,
                    },
                  });
                }}
              />
            </SuggestPriceToggleSection>
          </BottomSection>
        )}
      </ProductInfoSection>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  height: 160px;

  display: flex;
  justify-content: flex-start;
  align-items: flex-start;

  align-self: stretch;

  gap: 16px;

  cursor: pointer;
`;

const ImageWrap = styled.div`
  min-width: 150px;
  height: 100%;

  position: relative;

  border-radius: 8px;

  background-color: #f2f3f5;

  padding: 0px 16px;
`;

const Image = styled.img<{ isBlurred: boolean }>`
  width: 100%;
  height: 100%;

  left: 0;
  top: 0;

  object-fit: cover;

  position: absolute;

  border-radius: 8px;

  opacity: ${(props) => (props.isBlurred ? 0.4 : 1)};
`;

const BadgeContainer = styled.div`
  position: absolute;

  top: 8px;
  left: 8px;

  z-index: 50;

  display: flex;
  gap: 4px;
`;

const DimmedText = styled.span`
  position: absolute;

  left: 50%;
  top: 50%;

  transform: translate(-50%, -50%);

  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;
  font-family: Pretendard;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 85%; /* 20.4px */

  mix-blend-mode: darken;

  opacity: 0.4;

  white-space: nowrap;
`;

const ProductInfoSection = styled.div<{ isBlurred: boolean }>`
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 4px;

  opacity: ${(props) => (props.isBlurred ? 0.3 : 1)};
`;

const ProductContentGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;

  padding-top: 4px;
`;

const LocationAndTimeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--radius-large, 16px);
  align-self: stretch;
`;

const Title = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 20.8px */

  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;

  overflow: hidden;
`;

const Price = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 20.8px */
`;

const TractorSummaryText = styled.h2`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 18.2px */

  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  align-self: stretch;

  text-overflow: ellipsis;
`;

const BottomSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  gap: 10px;
  flex: 1 0 0;
  align-self: stretch;
`;

const SuggestPriceToggleSection = styled.div`
  display: flex;
  padding-top: 16px;
  padding-bottom: 4px;
  align-items: flex-start;
  gap: 2px;
  align-self: stretch;

  border-top: 1px solid var(--Light-Scheme-Monochrome-grey20, #ececf3);
`;

const SuggestPriceTextSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 2px;
  flex: 1 0 0;
`;

const SuggestPriceTitle = styled.span`
  align-self: stretch;

  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* System/Medium/m-bold */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 85%; /* 11.9px */
`;

const SuggestPriceDescription = styled.span`
  align-self: stretch;

  color: var(--Light-Scheme-Color-primary-blue, #196eff);

  /* Display/Small/s-medium */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 15.6px */
`;
